import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import { MenuOption, OptionsMenu } from 'framework/ui/menu';
import { ClientPortalTaskComment } from '../../types';
import { getInitials } from './commentUtils';
import CommentInlineForm from './CommentInlineForm';
import ReplyItem from './ReplyItem';
import { CommentActions } from './types';

type CommentCardProps = {
  comment: ClientPortalTaskComment;
  actions: CommentActions;
  isReadonly?: boolean;
};

const CommentCard = ({ comment, actions, isReadonly = false }: CommentCardProps) => {
  const isEditingComment = actions.activeForm?.type === 'edit-comment' && actions.activeForm.commentId === comment.id;
  const isReplying = actions.activeForm?.type === 'reply' && actions.activeForm.commentId === comment.id;

  const renderCommentMenu = () => {
    if (isReadonly || comment.authorId !== actions.currentUserAlias) {
      return null;
    }

    const options: MenuOption<ClientPortalTaskComment>[] = [
      {
        key: 'edit',
        label: 'Edit comment',
        onClick: () => actions.openEditCommentForm(comment),
      },
    ];

    return <OptionsMenu options={options} data={comment} />;
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        p: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, backgroundColor: 'primary.main', opacity: 0.7 }}>
              {getInitials(comment.authorName)}
            </Avatar>
            <Stack direction="column">
              <Typography color="primary" variant="subtitle1">
                {comment.authorName}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {actions.getRelativeDate(comment.createdAt)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {renderCommentMenu()}
      </Stack>

      {isEditingComment ? (
        <CommentInlineForm
          value={actions.activeForm?.text || ''}
          submitLabel="Save"
          onChange={actions.updateActiveFormText}
          onSubmit={actions.submitActiveForm}
          onCancel={actions.cancelActiveForm}
        />
      ) : (
        <Typography mt={2} whiteSpace="pre-wrap">
          {comment.text}
        </Typography>
      )}

      {comment.replies.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack>
            {comment.replies.map(reply => (
              <ReplyItem key={reply.id} reply={reply} actions={actions} isReadonly={isReadonly} />
            ))}
          </Stack>
        </>
      )}

      {!isReadonly && !isEditingComment && !isReplying && (
        <Box display="flex" justifyContent="flex-end">
          <Button variant="text" sx={{ mt: 1 }} onClick={() => actions.openReplyForm(comment.id)}>
            Reply in thread
          </Button>
        </Box>
      )}

      {isReplying && (
        <CommentInlineForm
          value={actions.activeForm?.text || ''}
          submitLabel="Add reply"
          onChange={actions.updateActiveFormText}
          onSubmit={actions.submitActiveForm}
          onCancel={actions.cancelActiveForm}
        />
      )}
    </Box>
  );
};

export default CommentCard;
